import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AddOutlined, Inventory2Outlined } from '@mui/icons-material';
import { listFamilyMembers } from '../../services/familyApi';
import { listDocuments } from '../../services/documentApi';
import { createOrder, listOrders, quoteOrder } from '../../services/ordersApi';
import { getTrip, listTrips } from '../../services/tripApi';

const languageNameFormatter = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
  ? new Intl.DisplayNames(['en'], { type: 'language' })
  : null;

function formatMemberName(member) {
  if (!member) {
    return '';
  }

  return [member.firstName, member.middleNames, member.lastName].filter(Boolean).join(' ');
}

function formatOrderType(value) {
  if (value === 'translation' || value === 'Translation') {
    return 'Notarised translation';
  }

  if (value === 'visa_appointment' || value === 'VisaAppointment') {
    return 'Visa Appointment Assistance';
  }

  if (value === 'print_package' || value === 'PrintPackage') {
    return 'Print Visa Package';
  }

  if (value === 'print_photos' || value === 'PrintPhotos') {
    return 'Print Photos';
  }

  return value ?? 'Order';
}

function formatStatusLabel(value) {
  return {
    Pending: 'Pending',
    InProgress: 'In progress',
    Delivering: 'Delivering',
    Done: 'Done',
  }[value] ?? value ?? 'Unknown';
}

function formatStatusColor(value) {
  return {
    Pending: 'warning',
    InProgress: 'info',
    Delivering: 'secondary',
    Done: 'success',
  }[value] ?? 'default';
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return value;
  }

  return parsedValue.toLocaleString();
}

function formatMoney(amount) {
  return `${amount ?? 0} EGP`;
}

function getDocumentSelectionKey(memberId, documentId) {
  return `${memberId}::${documentId}`;
}

function isTranslationOrder(value) {
  return value === 'translation' || value === 'Translation';
}

function isPrintPackageOrder(value) {
  return value === 'print_package' || value === 'PrintPackage';
}

function isPrintPhotosOrder(value) {
  return value === 'print_photos' || value === 'PrintPhotos';
}

function isDeliveryOrder(value) {
  return isTranslationOrder(value) || isPrintPackageOrder(value) || isPrintPhotosOrder(value);
}

function isImageDocument(document) {
  return Boolean(document?.contentType?.startsWith('image/'));
}

function formatLanguageName(value) {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) {
    return '';
  }

  const formattedName = languageNameFormatter?.of(normalizedValue.toLowerCase());
  if (formattedName) {
    return formattedName;
  }

  return normalizedValue.toUpperCase() === normalizedValue
    ? normalizedValue
    : normalizedValue.charAt(0).toUpperCase() + normalizedValue.slice(1);
}

function buildOrderIntentForm(intent) {
  const availableTargetLanguages = Array.isArray(intent?.metadata?.availableTargetLanguages)
    ? intent.metadata.availableTargetLanguages.filter(Boolean)
    : [];

  return {
    type: intent?.type ?? 'translation',
    typeLocked: Boolean(intent?.type),
    tripId: intent?.tripId ?? null,
    sourceRequirementUuid: intent?.sourceRequirementUuid ?? null,
    sourceRequirementTitle: intent?.sourceRequirementTitle ?? '',
    metadata: intent?.metadata ?? null,
    availableTargetLanguages,
    selectedPackageKey: intent?.metadata?.packageId && intent?.metadata?.memberId && intent?.tripId
      ? `${intent.tripId}::${intent.metadata.memberId}::${intent.metadata.packageId}`
      : '',
    targetLanguage:
      availableTargetLanguages.length > 0 && availableTargetLanguages.includes(intent?.metadata?.targetLanguage)
        ? intent.metadata.targetLanguage
        : '',
    address: '',
    phoneNumber: '',
    selectedDocumentKeys: Array.isArray(intent?.documents)
      ? intent.documents.map((document) => getDocumentSelectionKey(document.memberId, document.documentId))
      : [],
  };
}

const emptyComposerForm = buildOrderIntentForm(null);

function OrdersWorkspace({ account, initialIntent, onConsumeIntent }) {
  const [members, setMembers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [trips, setTrips] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [screenError, setScreenError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerStep, setComposerStep] = useState('details');
  const [composerForm, setComposerForm] = useState(emptyComposerForm);
  const [composerError, setComposerError] = useState('');
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [quote, setQuote] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const membersById = useMemo(
    () => Object.fromEntries(members.map((member) => [member.memberId, member])),
    [members]
  );

  const selectedDocumentRequests = useMemo(
    () =>
      composerForm.selectedDocumentKeys.map((key) => {
        const [memberId, documentId] = key.split('::');
        return { memberId, documentId };
      }),
    [composerForm.selectedDocumentKeys]
  );

  const selectedPackageRequest = useMemo(() => {
    if (!composerForm.selectedPackageKey) {
      return null;
    }

    const [tripId, memberId, packageId] = composerForm.selectedPackageKey.split('::');
    if (!tripId || !memberId || !packageId) {
      return null;
    }

    return { tripId, memberId, packageId };
  }, [composerForm.selectedPackageKey]);

  const documentsByMember = useMemo(() => {
    const grouped = {};

    for (const document of documents) {
      if (!grouped[document.memberId]) {
        grouped[document.memberId] = [];
      }

      grouped[document.memberId].push(document);
    }

    return grouped;
  }, [documents]);

  const availablePackages = useMemo(
    () =>
      trips.flatMap((trip) =>
        (trip.packages ?? []).map((packageItem) => {
          const member = membersById[packageItem.memberId];
          const destination = trip.destinationCountryName || trip.destinationCountryCode || 'Trip';
          const traveler = formatMemberName(member) || 'Traveler';
          const createdAtLabel = formatDateTime(packageItem.createdAtUtc);
          const sourceCount = packageItem.sourceDocumentNames?.length ?? 0;

          return {
            key: `${trip.tripId}::${packageItem.memberId}::${packageItem.packageId}`,
            tripId: trip.tripId,
            memberId: packageItem.memberId,
            packageId: packageItem.packageId,
            label: `${destination} • ${traveler} • ${createdAtLabel}`,
            helperText: `${sourceCount} document${sourceCount === 1 ? '' : 's'} in package`,
          };
        })
      ),
    [membersById, trips]
  );

  const selectedPackageOption = useMemo(
    () => availablePackages.find((packageOption) => packageOption.key === composerForm.selectedPackageKey) ?? null,
    [availablePackages, composerForm.selectedPackageKey]
  );

  const availablePhotoDocumentsByMember = useMemo(() => {
    const grouped = {};

    for (const member of members) {
      const memberDocuments = (documentsByMember[member.memberId] ?? []).filter(isImageDocument);
      if (memberDocuments.length > 0) {
        grouped[member.memberId] = memberDocuments;
      }
    }

    return grouped;
  }, [documentsByMember, members]);

  const selectedPhotoDocumentRequest = useMemo(() => {
    if (!isPrintPhotosOrder(composerForm.type) || composerForm.selectedDocumentKeys.length === 0) {
      return null;
    }

    const [memberId, documentId] = composerForm.selectedDocumentKeys[0].split('::');
    if (!memberId || !documentId) {
      return null;
    }

    return { memberId, documentId };
  }, [composerForm.selectedDocumentKeys, composerForm.type]);

  useEffect(() => {
    let ignore = false;

    async function loadWorkspace() {
      if (!account?.token) {
        setMembers([]);
        setDocuments([]);
        setTrips([]);
        setOrders([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setScreenError('');

      try {
        const [membersResponse, documentsResponse, ordersResponse, tripsResponse] = await Promise.all([
          listFamilyMembers({ token: account.token }),
          listDocuments({ token: account.token }),
          listOrders({ token: account.token }),
          listTrips({ token: account.token }),
        ]);

        const tripDetailsResponses = await Promise.all(
          (tripsResponse.trips ?? []).map((trip) => getTrip({ token: account.token, tripId: trip.tripId }))
        );

        if (ignore) {
          return;
        }

        setMembers(membersResponse.members ?? []);
        setDocuments(documentsResponse.documents ?? []);
        setTrips(tripDetailsResponses.map((response) => response.trip).filter(Boolean));
        setOrders(ordersResponse.orders ?? []);
      } catch (error) {
        if (!ignore) {
          setScreenError(error.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadWorkspace();
    return () => {
      ignore = true;
    };
  }, [account?.token]);

  useEffect(() => {
    if (!initialIntent) {
      return;
    }

    setComposerForm(buildOrderIntentForm(initialIntent));
    setComposerOpen(true);
    setComposerStep('details');
    setComposerError('');
    setQuote(null);
    setQuoteError('');
    setSuccessMessage('');
    onConsumeIntent?.();
  }, [initialIntent, onConsumeIntent]);

  useEffect(() => {
    let ignore = false;

    async function loadQuote() {
      if (!account?.token || !composerOpen) {
        return;
      }

      if (isTranslationOrder(composerForm.type) && selectedDocumentRequests.length === 0) {
        setQuote(null);
        setQuoteError('');
        return;
      }

      if (isPrintPackageOrder(composerForm.type) && !selectedPackageRequest) {
        setQuote(null);
        setQuoteError('');
        return;
      }

      if (isPrintPhotosOrder(composerForm.type) && !selectedPhotoDocumentRequest) {
        setQuote(null);
        setQuoteError('');
        return;
      }

      setQuoteBusy(true);
      setQuoteError('');

      try {
        const response = await quoteOrder({
          token: account.token,
          order: {
            type: composerForm.type,
            requestedDocuments: selectedDocumentRequests,
            metadata: isPrintPackageOrder(composerForm.type)
              ? {
                tripId: selectedPackageRequest?.tripId ?? composerForm.tripId ?? null,
                memberId: selectedPackageRequest?.memberId ?? null,
                packageId: selectedPackageRequest?.packageId ?? null,
              }
              : null,
          },
        });

        if (!ignore) {
          setQuote(response);
        }
      } catch (error) {
        if (!ignore) {
          setQuote(null);
          setQuoteError(error.message);
        }
      } finally {
        if (!ignore) {
          setQuoteBusy(false);
        }
      }
    }

    loadQuote();
    return () => {
      ignore = true;
    };
  }, [account?.token, composerForm.tripId, composerForm.type, composerOpen, selectedDocumentRequests, selectedPackageRequest, selectedPhotoDocumentRequest]);

  function openComposer(intent = null) {
    setComposerForm(buildOrderIntentForm(intent));
    setComposerOpen(true);
    setComposerStep('details');
    setComposerError('');
    setQuote(null);
    setQuoteError('');
    setSuccessMessage('');
  }

  function closeComposer() {
    if (placingOrder) {
      return;
    }

    resetComposerState();
  }

  function resetComposerState() {
    setComposerOpen(false);
    setComposerStep('details');
    setComposerForm(buildOrderIntentForm(null));
    setComposerError('');
    setQuote(null);
    setQuoteError('');
  }

  function toggleSelectedDocument(memberId, documentId, checked) {
    const key = getDocumentSelectionKey(memberId, documentId);
    setComposerForm((current) => ({
      ...current,
      selectedDocumentKeys: checked
        ? Array.from(new Set([...current.selectedDocumentKeys, key]))
        : current.selectedDocumentKeys.filter((value) => value !== key),
    }));
  }

  function validateComposer() {
    if (isTranslationOrder(composerForm.type)) {
      if (composerForm.selectedDocumentKeys.length === 0) {
        return 'Select at least one document.';
      }

      if (composerForm.availableTargetLanguages.length > 0 && !composerForm.targetLanguage) {
        return 'Select the translation language.';
      }
    }

    if (isPrintPackageOrder(composerForm.type) && !selectedPackageRequest) {
      return 'Select a visa package to print.';
    }

    if (isPrintPhotosOrder(composerForm.type) && (!selectedPhotoDocumentRequest || composerForm.selectedDocumentKeys.length !== 1)) {
      return 'Select one photo to print.';
    }

    if (isDeliveryOrder(composerForm.type) && !composerForm.address.trim()) {
      return 'Address is required.';
    }

    if (isDeliveryOrder(composerForm.type) && !composerForm.phoneNumber.trim()) {
      return 'Delivery phone number is required.';
    }

    if (!isDeliveryOrder(composerForm.type) && !composerForm.phoneNumber.trim()) {
      return 'Contact phone number is required.';
    }

    if (!quote) {
      return quoteBusy ? 'Still calculating the quote. Please wait a moment.' : 'Quote is not ready yet.';
    }

    return '';
  }

  function handleContinueToConfirmation() {
    const validationMessage = validateComposer();
    if (validationMessage) {
      setComposerError(validationMessage);
      return;
    }

    setComposerError('');
    setComposerStep('confirm');
  }

  async function handlePlaceOrder() {
    const validationMessage = validateComposer();
    if (validationMessage) {
      setComposerError(validationMessage);
      setComposerStep('details');
      return;
    }

    if (!account?.token) {
      return;
    }

    setPlacingOrder(true);
    setComposerError('');

    try {
      const createdOrder = await createOrder({
        token: account.token,
        order: {
          type: composerForm.type,
          tripId: selectedPackageRequest?.tripId ?? composerForm.tripId,
          sourceRequirementUuid: composerForm.sourceRequirementUuid,
          sourceRequirementTitle: composerForm.sourceRequirementTitle || null,
          deliveryDetails: isDeliveryOrder(composerForm.type)
            ? {
              address: composerForm.address.trim(),
              phoneNumber: composerForm.phoneNumber.trim(),
            }
            : {
              address: '',
              phoneNumber: composerForm.phoneNumber.trim(),
            },
          requestedDocuments:
            isTranslationOrder(composerForm.type) || isPrintPhotosOrder(composerForm.type)
              ? selectedDocumentRequests
              : [],
          metadata: {
            ...(composerForm.metadata ?? {}),
            ...(isPrintPackageOrder(composerForm.type)
              ? {
                tripId: selectedPackageRequest?.tripId ?? composerForm.tripId ?? null,
                memberId: selectedPackageRequest?.memberId ?? null,
                packageId: selectedPackageRequest?.packageId ?? null,
                packageLabel: selectedPackageOption?.label ?? '',
              }
              : {}),
            ...(isPrintPhotosOrder(composerForm.type) && selectedPhotoDocumentRequest
              ? {
                photoCount: 8,
                photoLabel:
                  quote?.requestedDocuments?.[0]?.fileName ||
                  documentsByMember[selectedPhotoDocumentRequest.memberId]?.find(
                    (document) => document.documentId === selectedPhotoDocumentRequest.documentId
                  )?.fileName ||
                  '',
              }
              : {}),
            ...(isTranslationOrder(composerForm.type) && composerForm.targetLanguage
              ? { targetLanguage: composerForm.targetLanguage }
              : {}),
          },
        },
      });

      setOrders((current) => [createdOrder, ...current]);
      setSelectedOrder(createdOrder);
      setSuccessMessage('Order placed successfully.');
      resetComposerState();
    } catch (error) {
      setComposerError(error.message);
    } finally {
      setPlacingOrder(false);
    }
  }

  return (
    <Stack spacing={3}>
      {screenError ? <Alert severity="error">{screenError}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Box
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          justifyContent: 'space-between',
        }}
      >

        <Button onClick={() => openComposer()} startIcon={<AddOutlined />} variant="contained">
          New order
        </Button>
      </Box>

      {loading ? (
        <Paper sx={{ p: 3 }} variant="outlined">
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress size={24} />
            <Typography color="text.secondary" variant="body2">
              Loading your orders...
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.length > 0 ? (
                orders.map((order) => (
                  <TableRow
                    hover
                    key={order.orderId}
                    onClick={() => setSelectedOrder(order)}
                    sx={{
                      cursor: 'pointer',
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                    }}
                  >
                    <TableCell sx={{ minWidth: 260 }}>
                      <Stack alignItems="center" direction="row" spacing={1.5}>
                        <Box
                          sx={{
                            alignItems: 'center',
                            bgcolor: 'background.default',
                            borderRadius: 1.5,
                            color: 'text.secondary',
                            display: 'flex',
                            height: 36,
                            justifyContent: 'center',
                            width: 36,
                          }}
                        >
                          <Inventory2Outlined fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600 }}>{formatOrderType(order.type)}</Typography>
                          <Typography color="text.secondary" noWrap variant="body2">
                            {order.sourceRequirementTitle || order.metadata?.packageLabel || order.metadata?.photoLabel || order.orderId}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        color={formatStatusColor(order.status)}
                        label={formatStatusLabel(order.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{order.requestedDocuments?.length ?? 0}</TableCell>
                    <TableCell>{formatMoney(order.amountBreakdown?.totalEgp)}</TableCell>
                    <TableCell>{formatDateTime(order.updatedAtUtc)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1">No orders yet</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Start with a notarised translation order here, request a visa appointment from an appointment requirement inside a trip, print one of your generated visa packages, or order printed photo copies.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog fullWidth maxWidth="md" onClose={closeComposer} open={composerOpen}>
        <DialogTitle>
          {composerStep === 'confirm'
            ? `Confirm ${formatOrderType(composerForm.type).toLowerCase()}`
            : `New ${formatOrderType(composerForm.type).toLowerCase()}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            {composerError ? <Alert severity="error">{composerError}</Alert> : null}
            {quoteError ? <Alert severity="error">{quoteError}</Alert> : null}

            {composerStep === 'details' ? (
              <>
                <Paper sx={{ p: 2.5 }} variant="outlined">
                  <Stack spacing={2}>
                    <TextField
                      disabled={composerForm.typeLocked}
                      fullWidth
                      label="Order type"
                      onChange={(event) =>
                        setComposerForm((current) => ({
                          ...current,
                          type: event.target.value,
                          selectedDocumentKeys:
                            event.target.value === 'print_photos'
                              ? current.selectedDocumentKeys.slice(0, 1)
                              : current.selectedDocumentKeys,
                        }))
                      }
                      select
                      value={composerForm.type}
                    >
                      <MenuItem value="translation">Notarised translation</MenuItem>
                      <MenuItem value="visa_appointment">Visa Appointment Assistance</MenuItem>
                      <MenuItem value="print_package">Print Visa Package</MenuItem>
                      <MenuItem value="print_photos">Print Photos</MenuItem>
                    </TextField>

                    {isTranslationOrder(composerForm.type) ? (
                      <>
                        {composerForm.availableTargetLanguages.length > 0 ? (
                          <TextField
                            fullWidth
                            label="Translate to"
                            onChange={(event) =>
                              setComposerForm((current) => ({ ...current, targetLanguage: event.target.value }))
                            }
                            select
                            value={composerForm.targetLanguage}
                          >
                            <MenuItem value="">Select target language</MenuItem>
                            {composerForm.availableTargetLanguages.map((language) => (
                              <MenuItem key={language} value={language}>
                                {formatLanguageName(language)}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : null}

                        <TextField
                          fullWidth
                          label="Address"
                          multiline
                          minRows={2}
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, address: event.target.value }))
                          }
                          value={composerForm.address}
                        />

                        <TextField
                          fullWidth
                          label="Delivery phone number"
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, phoneNumber: event.target.value }))
                          }
                          value={composerForm.phoneNumber}
                        />
                      </>
                    ) : isPrintPackageOrder(composerForm.type) ? (
                      <>
                        <TextField
                          fullWidth
                          helperText={
                            availablePackages.length > 0
                              ? 'Choose one of your generated visa packages.'
                              : 'Generate a visa package in Trips before placing this order.'
                          }
                          label="Visa package"
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, selectedPackageKey: event.target.value }))
                          }
                          select
                          value={composerForm.selectedPackageKey}
                        >
                          <MenuItem value="">Select a package</MenuItem>
                          {availablePackages.map((packageOption) => (
                            <MenuItem key={packageOption.key} value={packageOption.key}>
                              {packageOption.label}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          fullWidth
                          label="Address"
                          multiline
                          minRows={2}
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, address: event.target.value }))
                          }
                          value={composerForm.address}
                        />

                        <TextField
                          fullWidth
                          label="Delivery phone number"
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, phoneNumber: event.target.value }))
                          }
                          value={composerForm.phoneNumber}
                        />

                        {selectedPackageOption ? (
                          <Typography color="text.secondary" variant="body2">
                            Package: {selectedPackageOption.helperText}
                          </Typography>
                        ) : null}
                      </>
                    ) : isPrintPhotosOrder(composerForm.type) ? (
                      <>
                        <Typography color="text.secondary" variant="body2">
                          One selected image will be printed as 8 photo copies for 500 EGP, plus 85 EGP delivery.
                        </Typography>

                        <TextField
                          fullWidth
                          label="Address"
                          multiline
                          minRows={2}
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, address: event.target.value }))
                          }
                          value={composerForm.address}
                        />

                        <TextField
                          fullWidth
                          label="Delivery phone number"
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, phoneNumber: event.target.value }))
                          }
                          value={composerForm.phoneNumber}
                        />
                      </>
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          label="Contact phone number"
                          onChange={(event) =>
                            setComposerForm((current) => ({ ...current, phoneNumber: event.target.value }))
                          }
                          value={composerForm.phoneNumber}
                        />
                        <Typography variant="body2">
                          Requirement: {composerForm.sourceRequirementTitle || 'Visa appointment'}
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Paper>

                {isTranslationOrder(composerForm.type) ? (
                  <Paper sx={{ p: 2.5 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2">Requested documents</Typography>
                      <Typography color="text.secondary" variant="body2">
                        We preselect documents when you arrive from a requirement, and you can still switch to any document in your library here.
                      </Typography>

                      {members.length === 0 ? (
                        <Alert severity="warning">Add a family member and documents before placing an order.</Alert>
                      ) : (
                        <Stack spacing={1.25}>
                          {members.map((member) => {
                            const memberDocuments = documentsByMember[member.memberId] ?? [];

                            return (
                              <Paper key={member.memberId} sx={{ p: 1.5 }} variant="outlined">
                                <Stack spacing={1}>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {formatMemberName(member) || 'Traveler'}
                                  </Typography>
                                  {memberDocuments.length > 0 ? (
                                    <Stack spacing={0.75}>
                                      {memberDocuments.map((document) => {
                                        const selectionKey = getDocumentSelectionKey(member.memberId, document.documentId);
                                        const checked = composerForm.selectedDocumentKeys.includes(selectionKey);

                                        return (
                                          <Box
                                            key={document.documentId}
                                            sx={{
                                              alignItems: { xs: 'flex-start', sm: 'center' },
                                              display: 'flex',
                                              flexDirection: { xs: 'column', sm: 'row' },
                                              gap: 1,
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Button
                                              onClick={() =>
                                                toggleSelectedDocument(member.memberId, document.documentId, !checked)
                                              }
                                              size="small"
                                              sx={{ justifyContent: 'flex-start', px: 0 }}
                                              variant="text"
                                            >
                                              {checked ? 'Remove' : 'Add'}
                                            </Button>
                                            <Typography sx={{ flex: 1 }} variant="body2">
                                              {document.fileName}
                                            </Typography>
                                            <Chip
                                              label={checked ? 'Selected' : 'Available'}
                                              size="small"
                                              variant={checked ? 'filled' : 'outlined'}
                                            />
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography color="text.secondary" variant="body2">
                                      No documents in the library for this traveler yet.
                                    </Typography>
                                  )}
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ) : null}

                {isPrintPhotosOrder(composerForm.type) ? (
                  <Paper sx={{ p: 2.5 }} variant="outlined">
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2">Select photo</Typography>
                      <Typography color="text.secondary" variant="body2">
                        Choose one image from your document library. We&apos;ll print 8 copies from this photo.
                      </Typography>

                      {members.length === 0 ? (
                        <Alert severity="warning">Add a family member and upload a photo before placing this order.</Alert>
                      ) : (
                        <Stack spacing={1.25}>
                          {members.map((member) => {
                            const memberDocuments = availablePhotoDocumentsByMember[member.memberId] ?? [];

                            return (
                              <Paper key={member.memberId} sx={{ p: 1.5 }} variant="outlined">
                                <Stack spacing={1}>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {formatMemberName(member) || 'Traveler'}
                                  </Typography>
                                  {memberDocuments.length > 0 ? (
                                    <Stack spacing={0.75}>
                                      {memberDocuments.map((document) => {
                                        const selectionKey = getDocumentSelectionKey(member.memberId, document.documentId);
                                        const checked = composerForm.selectedDocumentKeys.includes(selectionKey);

                                        return (
                                          <Box
                                            key={document.documentId}
                                            sx={{
                                              alignItems: { xs: 'flex-start', sm: 'center' },
                                              display: 'flex',
                                              flexDirection: { xs: 'column', sm: 'row' },
                                              gap: 1,
                                              justifyContent: 'space-between',
                                            }}
                                          >
                                            <Button
                                              onClick={() =>
                                                setComposerForm((current) => ({
                                                  ...current,
                                                  selectedDocumentKeys: checked ? [] : [selectionKey],
                                                }))
                                              }
                                              size="small"
                                              sx={{ justifyContent: 'flex-start', px: 0 }}
                                              variant="text"
                                            >
                                              {checked ? 'Selected' : 'Choose'}
                                            </Button>
                                            <Typography sx={{ flex: 1 }} variant="body2">
                                              {document.fileName}
                                            </Typography>
                                            <Chip
                                              label={checked ? 'Selected' : 'Available'}
                                              size="small"
                                              variant={checked ? 'filled' : 'outlined'}
                                            />
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  ) : (
                                    <Typography color="text.secondary" variant="body2">
                                      No image files are available for this traveler yet.
                                    </Typography>
                                  )}
                                </Stack>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ) : null}

                <Paper sx={{ p: 2.5 }} variant="outlined">
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Live quote</Typography>
                    {quoteBusy ? (
                      <Stack alignItems="center" direction="row" spacing={1}>
                        <CircularProgress size={16} />
                        <Typography color="text.secondary" variant="body2">
                          {isTranslationOrder(composerForm.type)
                            ? 'Calculating translation pages and price...'
                            : isPrintPackageOrder(composerForm.type)
                              ? 'Calculating print pages and delivery fee...'
                              : isPrintPhotosOrder(composerForm.type)
                                ? 'Calculating photo print bundle and delivery fee...'
                              : 'Calculating service fee...'}
                        </Typography>
                      </Stack>
                    ) : quote ? (
                      <>
                        {isDeliveryOrder(composerForm.type) ? (
                          <>
                            {isPrintPhotosOrder(composerForm.type) ? (
                              <Typography color="text.secondary" variant="body2">
                                8 printed photos: {formatMoney(quote.amountBreakdown.serviceSubtotalEgp)}
                              </Typography>
                            ) : (
                              <Typography color="text.secondary" variant="body2">
                                {isTranslationOrder(composerForm.type) ? 'Translation' : 'Printing'} amount (cash): {quote.amountBreakdown.totalPageCount} page
                                {quote.amountBreakdown.totalPageCount === 1 ? '' : 's'} x{' '}
                                {formatMoney(quote.amountBreakdown.pricePerPageEgp)} ={' '}
                                {formatMoney(quote.amountBreakdown.serviceSubtotalEgp)}
                              </Typography>
                            )}
                            <Typography color="text.secondary" variant="body2">
                              Delivery fees: {formatMoney(quote.amountBreakdown.deliveryFeesEgp)}
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                              Estimated delivery day: {quote.deliveryDetails?.estimatedDeliveryDate || 'Unknown'}
                            </Typography>
                          </>
                        ) : (
                          <Typography color="text.secondary" variant="body2">
                            Visa appointment service fee: {formatMoney(quote.amountBreakdown.serviceSubtotalEgp)}
                          </Typography>
                        )}
                        <Typography sx={{ fontWeight: 700 }} variant="body2">
                          Total: {formatMoney(quote.amountBreakdown.totalEgp)}
                        </Typography>
                      </>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        {isTranslationOrder(composerForm.type)
                          ? 'Select at least one document to generate a quote.'
                          : isPrintPackageOrder(composerForm.type)
                            ? 'Select a visa package to generate a quote.'
                            : isPrintPhotosOrder(composerForm.type)
                              ? 'Select one image to generate a quote.'
                            : 'Quote is not ready yet.'}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              </>
            ) : (
              <Paper sx={{ p: 2.5 }} variant="outlined">
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Order confirmation</Typography>
                  <Typography color="text.secondary" variant="body2">
                    Please review everything below before placing the order.
                  </Typography>
                  <Divider />
                  {isTranslationOrder(composerForm.type) ? (
                    <>
                      {composerForm.targetLanguage ? (
                        <Typography variant="body2">
                          Translate to: {formatLanguageName(composerForm.targetLanguage)}
                        </Typography>
                      ) : null}
                      <Typography variant="body2">Address: {composerForm.address}</Typography>
                      <Typography variant="body2">Delivery phone number: {composerForm.phoneNumber}</Typography>
                      <Typography variant="body2">
                        Translation amount (cash): {formatMoney(quote?.amountBreakdown?.serviceSubtotalEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Delivery fees: {formatMoney(quote?.amountBreakdown?.deliveryFeesEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Estimated delivery day: {quote?.deliveryDetails?.estimatedDeliveryDate || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        Requested documents: {(quote?.requestedDocuments ?? []).length}
                      </Typography>
                      <Stack spacing={0.75}>
                        {(quote?.requestedDocuments ?? []).map((document) => (
                          <Typography key={`${document.memberId}-${document.documentId}`} color="text.secondary" variant="body2">
                            {document.fileName} • {document.pageCount} page{document.pageCount === 1 ? '' : 's'} •{' '}
                            {formatMemberName(membersById[document.memberId]) || document.memberId}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  ) : isPrintPackageOrder(composerForm.type) ? (
                    <>
                      <Typography variant="body2">
                        Package: {selectedPackageOption?.label || composerForm.metadata?.packageLabel || 'Visa package'}
                      </Typography>
                      <Typography variant="body2">Address: {composerForm.address}</Typography>
                      <Typography variant="body2">Delivery phone number: {composerForm.phoneNumber}</Typography>
                      <Typography variant="body2">
                        Printing amount (cash): {formatMoney(quote?.amountBreakdown?.serviceSubtotalEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Delivery fees: {formatMoney(quote?.amountBreakdown?.deliveryFeesEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Estimated delivery day: {quote?.deliveryDetails?.estimatedDeliveryDate || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        Included documents: {(quote?.requestedDocuments ?? []).length}
                      </Typography>
                      <Stack spacing={0.75}>
                        {(quote?.requestedDocuments ?? []).map((document) => (
                          <Typography key={`${document.memberId}-${document.documentId}`} color="text.secondary" variant="body2">
                            {document.fileName} • {document.pageCount} page{document.pageCount === 1 ? '' : 's'} •{' '}
                            {formatMemberName(membersById[document.memberId]) || document.memberId}
                          </Typography>
                        ))}
                      </Stack>
                    </>
                  ) : isPrintPhotosOrder(composerForm.type) ? (
                    <>
                      <Typography variant="body2">Address: {composerForm.address}</Typography>
                      <Typography variant="body2">Delivery phone number: {composerForm.phoneNumber}</Typography>
                      <Typography variant="body2">
                        8 printed photos: {formatMoney(quote?.amountBreakdown?.serviceSubtotalEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Delivery fees: {formatMoney(quote?.amountBreakdown?.deliveryFeesEgp)}
                      </Typography>
                      <Typography variant="body2">
                        Estimated delivery day: {quote?.deliveryDetails?.estimatedDeliveryDate || 'Unknown'}
                      </Typography>
                      <Typography variant="body2">
                        Selected photo: {quote?.requestedDocuments?.[0]?.fileName || 'Unknown'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2">Contact phone number: {composerForm.phoneNumber}</Typography>
                      <Typography variant="body2">
                        Requirement: {composerForm.sourceRequirementTitle || 'Visa appointment'}
                      </Typography>
                      <Typography variant="body2">
                        Service fee: {formatMoney(quote?.amountBreakdown?.serviceSubtotalEgp)}
                      </Typography>
                    </>
                  )}
                  <Divider />
                  <Typography sx={{ fontWeight: 700 }} variant="body1">
                    {isDeliveryOrder(composerForm.type) ? 'Total due on delivery' : 'Total'}:{' '}
                    {formatMoney(quote?.amountBreakdown?.totalEgp)}
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {composerStep === 'confirm' ? (
            <>
              <Button disabled={placingOrder} onClick={() => setComposerStep('details')}>
                Back
              </Button>
              <Button disabled={placingOrder} onClick={closeComposer}>
                Cancel
              </Button>
              <Button disabled={placingOrder} onClick={handlePlaceOrder} variant="contained">
                {placingOrder ? 'Placing...' : 'Place order'}
              </Button>
            </>
          ) : (
            <>
              <Button disabled={placingOrder} onClick={closeComposer}>
                Cancel
              </Button>
              <Button disabled={placingOrder} onClick={handleContinueToConfirmation} variant="contained">
                Continue to confirmation
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog fullWidth maxWidth="md" onClose={() => setSelectedOrder(null)} open={Boolean(selectedOrder)}>
        <DialogTitle>{formatOrderType(selectedOrder?.type)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={1}>
                <Typography variant="subtitle2">Order status</Typography>
                <Chip
                  color={formatStatusColor(selectedOrder?.status)}
                  label={formatStatusLabel(selectedOrder?.status)}
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  variant="outlined"
                />
                <Typography color="text.secondary" variant="body2">
                  Created: {formatDateTime(selectedOrder?.createdAtUtc)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Last updated: {formatDateTime(selectedOrder?.updatedAtUtc)}
                </Typography>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={1}>
                <Typography variant="subtitle2">Delivery</Typography>
                {selectedOrder?.deliveryDetails ? (
                  <>
                    {isDeliveryOrder(selectedOrder?.type) ? (
                      <Typography color="text.secondary" variant="body2">
                        Address: {selectedOrder.deliveryDetails.address || 'Not provided'}
                      </Typography>
                    ) : null}
                    <Typography color="text.secondary" variant="body2">
                      {isDeliveryOrder(selectedOrder?.type) ? 'Phone' : 'Contact phone'}: {selectedOrder.deliveryDetails.phoneNumber || 'Not provided'}
                    </Typography>
                    {isDeliveryOrder(selectedOrder?.type) ? (
                      <Typography color="text.secondary" variant="body2">
                        Estimated delivery day: {selectedOrder.deliveryDetails.estimatedDeliveryDate || 'Unknown'}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    No delivery step is required for this order type.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={1}>
                <Typography variant="subtitle2">Amount breakdown</Typography>
                <Typography color="text.secondary" variant="body2">
                  Service: {formatMoney(selectedOrder?.amountBreakdown?.serviceSubtotalEgp)}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Delivery: {formatMoney(selectedOrder?.amountBreakdown?.deliveryFeesEgp)}
                </Typography>
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  Total: {formatMoney(selectedOrder?.amountBreakdown?.totalEgp)}
                </Typography>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2.5 }} variant="outlined">
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {isTranslationOrder(selectedOrder?.type) || isPrintPackageOrder(selectedOrder?.type) || isPrintPhotosOrder(selectedOrder?.type)
                    ? 'Requested documents'
                    : 'Order details'}
                </Typography>
                {isTranslationOrder(selectedOrder?.type) ? (
                  <>
                    {selectedOrder?.metadata?.targetLanguage ? (
                      <Typography color="text.secondary" variant="body2">
                        Translate to: {formatLanguageName(selectedOrder.metadata.targetLanguage)}
                      </Typography>
                    ) : null}
                    {(selectedOrder?.requestedDocuments ?? []).map((document) => (
                      <Typography key={`${document.memberId}-${document.documentId}`} color="text.secondary" variant="body2">
                        {document.fileName} • {document.pageCount} page{document.pageCount === 1 ? '' : 's'} •{' '}
                        {formatMemberName(membersById[document.memberId]) || document.memberId}
                      </Typography>
                    ))}
                  </>
                ) : isPrintPackageOrder(selectedOrder?.type) ? (
                  <>
                    <Typography color="text.secondary" variant="body2">
                      Package: {selectedOrder?.metadata?.packageLabel || 'Visa package'}
                    </Typography>
                    {(selectedOrder?.requestedDocuments ?? []).map((document) => (
                      <Typography key={`${document.memberId}-${document.documentId}`} color="text.secondary" variant="body2">
                        {document.fileName} • {document.pageCount} page{document.pageCount === 1 ? '' : 's'} •{' '}
                        {formatMemberName(membersById[document.memberId]) || document.memberId}
                      </Typography>
                    ))}
                  </>
                ) : isPrintPhotosOrder(selectedOrder?.type) ? (
                  <>
                    <Typography color="text.secondary" variant="body2">
                      Photo bundle: {(selectedOrder?.metadata?.photoCount ?? 8)} prints
                    </Typography>
                    {(selectedOrder?.requestedDocuments ?? []).map((document) => (
                      <Typography key={`${document.memberId}-${document.documentId}`} color="text.secondary" variant="body2">
                        {document.fileName} • {formatMemberName(membersById[document.memberId]) || document.memberId}
                      </Typography>
                    ))}
                  </>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    Requirement: {selectedOrder?.sourceRequirementTitle || 'Visa appointment'}
                  </Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default OrdersWorkspace;
